import { useRoute } from "@react-navigation/core";
import React, { memo, useEffect } from "react";
import { ActivityIndicator, FlatList, Text, View } from "react-native";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { fetchGetEventInterestedUsers } from "../actions/chat";
import ChatUserItem from "../components/ChatUserItem";
import LiveEvent from "../components/LiveEvent";
import {
	GROUP_ID_ACTIVITY,
	GROUP_ID_GOODDEAL_ACTIVITY,
	GROUP_ID_NEW_USER,
	GROUP_ID_PROPOSAL,
	GROUP_ID_TEMPLATE,
	ROLE_WEBMASTER
} from "../config/constants";
import {
	getCommunityReducer,
	getEventInterestedUsersReducer,
	getProfileReducer
} from "../selectors";
import { t } from "../services/i18n";
import commonStyles, { COLOR2 } from "../styles/commonStyles";

const getSelector = createSelector(
	[getProfileReducer, getEventInterestedUsersReducer, getCommunityReducer],
	({ user }, { isFetching, reload, event, users }, { channels }) => ({
		isFetching,
		reload,
		event,
		users,
		user,
		channels
	})
);

const EventInterestedUsers = () => {
	const dispatch = useDispatch();
	const route = useRoute();

	const { isFetching, reload, event, users, user, channels } = useSelector(
		getSelector,
		shallowEqual
	);

	let groupId,
		userId,
		channelsIds = [];
	if (event) {
		({ groupId, userId, eventId: { channelsIds = [] } = {} } = event);
	}

	// Resolved, non-empty publication targets. Used to hide the whole
	// "Cible de la publication" section when there is no target to show.
	const targetNames = (channelsIds || [])
		.map(channelId => channels?.[channelId]?.communityId?.name)
		.filter(Boolean);

	if (user && users) {
		if (user.role === ROLE_WEBMASTER) {
			delete users[userId];
		} else {
			// Delete current user
			delete users[user._id];
		}
	}

	useEffect(() => {
		dispatch(fetchGetEventInterestedUsers(route.params.id));
	}, []);

	useEffect(() => {
		if (reload) {
			dispatch(fetchGetEventInterestedUsers(route.params.id));
		}
	}, [reload]);

	const {
		font,
		flex1,
		justifyContentCenter,
		mb10,
		pv20,
		bgWhite,
		mt5,
		mv5,
		fontBold,
		shadowGrey,
		bgColor1,
		color5,
		w100p,
		textCenter
	} = commonStyles;

	const renderHeader = () => (
		<>
			<LiveEvent item={event} isDetails={true} />
			{targetNames.length > 0 &&
				user &&
				userId &&
				(user._id === userId || user?.role === ROLE_WEBMASTER) && (
					<View style={[flex1, pv20, bgWhite, mv5, shadowGrey]}>
						<Text style={[font, color5, textCenter, mb10, { fontSize: 17 }]}>
							{t("live:targetpublication")}
						</Text>
						{channelsIds
							.filter(channelId => channels?.[channelId]?.communityId?.name)
							.map(channelId => (
								<Text key={channelId} style={[fontBold, textCenter]}>
									{channels[channelId].communityId.name}
								</Text>
							))}
					</View>
				)}
			{Object.values(users).length > 0 &&
				user &&
				userId &&
				(user._id === userId || user?.role === ROLE_WEBMASTER) && (
					<View style={[flex1, pv20, bgWhite, mt5, shadowGrey]}>
						<Text style={[font, color5, textCenter, mb10, { fontSize: 17 }]}>
							{groupId === GROUP_ID_ACTIVITY ||
							groupId === GROUP_ID_PROPOSAL ||
							groupId === GROUP_ID_GOODDEAL_ACTIVITY
								? t("button:answers")
								: groupId === GROUP_ID_TEMPLATE
								? t("button:interested")
								: groupId === GROUP_ID_NEW_USER
								? t("live:answerswelcome")
								: t("live:answerslike")}
						</Text>
					</View>
				)}
		</>
	);

	return (
		<View style={[flex1, bgColor1]}>
			{isFetching || !event ? (
				<ActivityIndicator
					style={[flex1, justifyContentCenter]}
					size="large"
					color={COLOR2}
				/>
			) : (
				<FlatList
					style={[flex1]}
					data={
						Object.values(users).length > 0 &&
						user &&
						userId &&
						(user._id === userId || user?.role === ROLE_WEBMASTER)
							? Object.values(users)
							: []
					}
					renderItem={({ item }) => <ChatUserItem item={item} />}
					keyExtractor={obj => obj._id}
					ListHeaderComponent={renderHeader}
				/>
			)}
		</View>
	);
};

const MemoizedEventInterestedUsers = memo(EventInterestedUsers);
export default MemoizedEventInterestedUsers;
